import { META_PIXEL_ID, CAPI_ENDPOINT } from './config'

/**
 * Safe tracking layer.
 * While META_PIXEL_ID is empty every function is a no-op that logs to the console.
 * No pixel ID, webhook, or endpoint is ever hardcoded here.
 */

type Props = Record<string, string | number | boolean | undefined>

declare global {
  interface Window {
    fbq?: any
    _fbq?: any
  }
}

let pixelInitialized = false

function pixelReady(): boolean {
  return Boolean(META_PIXEL_ID) && pixelInitialized
}

/** Meta STANDARD events — must use fbq('track', ...). */
function sendStandard(event: string, props?: Props): void {
  if (!pixelReady()) {
    // eslint-disable-next-line no-console
    console.log('[tracking:no-op] standard', event, props ?? {})
    return
  }
  window.fbq?.('track', event, props)
}

/** Everything else is a custom event — fbq('trackCustom', ...). */
function send(event: string, props?: Props): void {
  if (!pixelReady()) {
    // eslint-disable-next-line no-console
    console.log('[tracking:no-op] custom', event, props ?? {})
    return
  }
  window.fbq?.('trackCustom', event, props)
}

/** Initializes the pixel exactly once. Does nothing while META_PIXEL_ID is empty. */
export function initTracking(): void {
  if (pixelInitialized) return
  pixelInitialized = true
  if (!META_PIXEL_ID) {
    // eslint-disable-next-line no-console
    console.log('[tracking:no-op] init skipped — META_PIXEL_ID is empty')
    return
  }
  if (typeof window === 'undefined') return

  // Standard Meta Pixel bootstrap.
  if (!window.fbq) {
    const n: any = function (...args: unknown[]) {
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args)
    }
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []
    window.fbq = n
    window._fbq = n

    const script = document.createElement('script')
    script.async = true
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'
    document.head.appendChild(script)
  }

  window.fbq?.('init', META_PIXEL_ID)
  window.fbq?.('track', 'PageView')
}

/* ---------------------------------------------------------------------------
 * ACTIVE EVENTS
 * PageView · ViewContent · Contact (standard)
 * VehicleInterest · ScrollDepth 50/75 (custom)
 * ------------------------------------------------------------------------- */

export const trackPageView = () => sendStandard('PageView')
export const trackViewContent = (name: string) =>
  sendStandard('ViewContent', { content_name: name })
/** The conversion event of this page: the visitor opened the GHL chat widget. */
export const trackContact = (context: string) => sendStandard('Contact', { context })
export const trackLead = (props: Props) => sendStandard('Lead', props)
export const trackVehicleInterest = (category: string) => send('VehicleInterest', { category })
export const trackScrollDepth = (depth: 50 | 75) => send('ScrollDepth', { depth })

/* ---------------------------------------------------------------------------
 * DORMANT EVENTS
 * Defined but intentionally NOT invoked from any component. Re-enable by
 * calling them again — no rewrite needed.
 * ------------------------------------------------------------------------- */

export const trackHeaderCTA = () => send('HeaderCTA')
export const trackHeroCTA = () => send('HeroCTA')
export const trackFormView = () => send('FormView')
export const trackFormStart = () => send('FormStart')
export const trackTimeOnPage = (seconds: 30 | 60 | 120) => send('TimeOnPage', { seconds })

/**
 * Server-side Meta Conversions API stub.
 * Uses the SAME eventId as the browser event so the two deduplicate.
 * Intentionally inert until CAPI_ENDPOINT is filled and a server route exists.
 */
export async function sendConversionsApiEvent(payload: {
  eventId: string
  [key: string]: unknown
}): Promise<void> {
  if (!CAPI_ENDPOINT) {
    // eslint-disable-next-line no-console
    console.log('[capi:no-op] CAPI_ENDPOINT is empty', payload.eventId)
    return
  }
  // await fetch(CAPI_ENDPOINT, {
  //   method: 'POST',
  //   headers: { 'Content-Type': 'application/json' },
  //   body: JSON.stringify({ ...payload, event_name: 'Lead', event_id: payload.eventId }),
  // })
}
